"use client";

import LazyBrowserModal from "@/components/BrowserModal/LazyBrowserModal";
import type { Price } from "@/types/price";
import { formatMonthlyPrice } from "./formatPrice";
import OfferList from "./OfferList";
import usePricingBoxState from "./usePricingBoxState";

interface PricingBoxProps {
  product: Price;
  currentPlan: string;
  isHighlighted: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const planDescriptions: Record<string, string> = {
  free: "For one person starting a watch list: 3 accounts checked weekly, plus quick research and outreach drafts.",
  pro: "For anyone running outbound against a real account list: 100 watched accounts, checks every 48 hours, and 90 days of history.",
  team: "For larger target lists: 1000 watched accounts, daily checks, a full year of change history, and the biggest batch limits.",
};

const PricingBox = ({ product, currentPlan, isHighlighted, onMouseEnter, onMouseLeave }: PricingBoxProps) => {
  const { buttonConfig, handleAction, installModalOpen, setInstallModalOpen } = usePricingBoxState(product, currentPlan);
  const planName = product.nickname?.toLowerCase() || "free";
  const priceDisplay = formatMonthlyPrice(product);
  const planBadge = product.nickname === "Pro" ? "Popular" : null;

  return (
    <>
      <div className="flex w-full flex-col items-stretch px-4 md:w-1/2 lg:w-1/3">
        <div
          className={`relative z-10 mb-10 flex h-full flex-col overflow-hidden rounded-2xl bg-white px-8 py-10 transition duration-300 hover:shadow-2xl dark:bg-dark sm:p-12 lg:px-8 lg:py-12 xl:p-12 ${
            isHighlighted
              ? "z-20 border border-primary shadow-xl md:scale-105 lg:scale-[1.03] dark:border-accent "
              : "border border-black/8 dark:border-white/10"
          }`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {isHighlighted && <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />}
          {planBadge && (
            <>
              <p className="absolute right-4 top-4 inline-block rounded-full bg-primary px-4 py-1 text-xs font-semibold text-white sm:hidden">
                {planBadge}
              </p>
              <p className="absolute -right-8 top-[60px] hidden rotate-45 rounded-md bg-primary px-8 py-1.5 text-xs font-semibold text-white sm:inline-block">
                {planBadge}
              </p>
            </>
          )}
          <span className="mb-3 block text-sm font-bold uppercase tracking-[0.22em] text-muted dark:text-accent">{product.nickname}</span>
          <h2 className="display text-4xl font-extrabold text-ink dark:text-white xl:text-[40px] xl:leading-[1.1]">{priceDisplay}</h2>
          <p className="mt-4 text-sm leading-7 text-muted dark:text-white/65">{planDescriptions[planName]}</p>
          <div className="mb-8 mt-8 grow">
            <h3 className="mb-6 border-b border-black/8 pb-4 text-base font-semibold text-ink dark:border-white/10 dark:text-white">
              Included Features
            </h3>
            <div className="mb-8 text-muted dark:text-white/70">
              {product.offers.map((offer, index) => {
                const offerText = typeof offer === "string" ? offer : offer.text;
                const available = typeof offer === "string" ? true : (offer.available ?? true);
                return <OfferList key={index} text={offerText} available={available} />;
              })}
            </div>
          </div>
          <button
            onClick={handleAction}
            disabled={buttonConfig.disabled}
            className={`mt-auto inline-block w-full rounded-full px-7 py-3.5 text-center text-sm font-semibold transition-all duration-300 ${
              buttonConfig.disabled
                ? "cursor-not-allowed border border-black/8 bg-black/[0.04] text-muted dark:border-white/10 dark:bg-white/5 dark:text-white/40"
                : isHighlighted
                  ? "bg-primary text-white hover:bg-primary-deep"
                  : "border border-black/8 bg-black/[0.04] text-ink hover:bg-black/[0.07] dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
            }`}
          >
            {buttonConfig.text}
          </button>
        </div>
      </div>
      <LazyBrowserModal isOpen={installModalOpen} onClose={() => setInstallModalOpen(false)} />
    </>
  );
};

export default PricingBox;
